import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { useAuthStore } from "@/features/auth/store";
import { apiErrorMessage } from "@/lib/api/errors";

import { api } from "@/lib/api/client";

import { organisationsApi, type Organisation, type OrganisationFilters } from "./api";

const KEY = "organisations";

export function useOrganisations(filters: OrganisationFilters = {}) {
  return useQuery({
    queryKey: [KEY, "list", filters],
    queryFn: () => organisationsApi.list(filters),
    placeholderData: (prev) => prev,
  });
}

/** Cliniques accessibles + clinique active (source partagée navbar/bandeau). */
export function useAccessibleOrganisations() {
  return useQuery({
    queryKey: [KEY, "accessible"],
    queryFn: organisationsApi.accessible,
    staleTime: 30_000,
  });
}

/** La session est-elle en « Toutes les cliniques » (ADR 0065) ? Lu sur `me`,
 *  disponible dès le premier rendu : les écrans propres à une clinique ne
 *  s'affichent jamais, même un instant, sur la clinique principale. */
export function useAccountScope(): boolean {
  return useAuthStore((s) => s.me?.account_scope === true);
}

/** Nom de la clinique active (fonctionne aussi pour un super_admin sans membership). */
export function useActiveClinicName(): string | undefined {
  const { data } = useAccessibleOrganisations();
  if (!data) return undefined;
  return data.organisations.find((o) => o.id === data.active_organisation_id)?.name;
}

/**
 * Fiche complète de la clinique active — source des en-têtes et pieds de
 * documents imprimés (logo, nom, adresse, téléphones, agrément).
 *
 * Passe par /accessible/, ouvert à tout membre, et NON par
 * /organisations/{id}/ qui est réservé au plan éditeur : ce dernier répond 403
 * à une secrétaire ou un médecin, et l'ordonnance sortait alors sans identité
 * de clinique (pictogramme de repli, « — » à la place du nom et de l'adresse).
 */
export function useActiveOrganisation(): { data: Organisation | null; isFetched: boolean } {
  const query = useAccessibleOrganisations();
  const data = query.data;
  const active = data
    ? (data.organisations.find((o) => o.id === data.active_organisation_id) ?? null)
    : null;
  return { data: active, isFetched: query.isFetched };
}

export function useOrganisation(id: string | undefined) {
  return useQuery({
    queryKey: [KEY, "detail", id],
    queryFn: () => organisationsApi.get(id as string),
    enabled: !!id,
  });
}

export function useOrganisationStats(id: string | undefined) {
  return useQuery({
    queryKey: [KEY, "stats", id],
    queryFn: () => organisationsApi.stats(id as string),
    enabled: !!id,
  });
}

export function useUploadOrganisationLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => organisationsApi.uploadLogo(id, file),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [KEY] });
      await qc.invalidateQueries({ queryKey: ["auth", "me"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Échec du téléversement du logo.")),
  });
}

export function useCreateOrganisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: organisationsApi.create,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [KEY] });
      toast.success("Clinique créée.");
    },
  });
}

export function useDeleteOrganisation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete(`/organisations/${id}/`);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [KEY] });
      toast.success("Clinique désactivée.");
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Suppression impossible.")),
  });
}

export function useUpdateOrganisation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof organisationsApi.update>[1]) =>
      organisationsApi.update(id, payload),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [KEY] });
      toast.success("Clinique mise à jour.");
    },
  });
}
